<template>
  <ModalView
    v-model="show"
    title="Edit application answers"
    :subtitle="licenseSubtitle"
    size="lg"
    scrollable
    ok-title="Save answers"
    :busy="saving"
    :disable-ok="!canSave"
    @ok="save"
  >
    <div class="d-flex flex-column gap-3">
      <div class="small text-muted">
        Changing an answer may reveal or hide related questions. Existing file uploads and signatures can be viewed but cannot be replaced here.
      </div>

      <div v-if="error" class="alert alert-danger mb-0" role="alert">
        {{ error.message || String(error) }}
      </div>

      <div v-if="loadingForm" class="d-flex align-items-center text-muted">
        <SpinnerView small class="me-2" />
        Loading application answers...
      </div>
      <SurveyComponent v-else-if="survey" :model="survey" />

      <div>
        <label for="application-answer-note" class="form-label small text-muted">
          Describe this change (optional)
        </label>
        <textarea
          id="application-answer-note"
          v-model="note"
          class="form-control form-control-sm"
          rows="2"
          :disabled="saving"
        />
      </div>
    </div>
  </ModalView>
</template>

<script>
import { SurveyComponent } from 'survey-vue3-ui';
import ModalView from '@/components/common/ModalView.vue';
import SpinnerView from '@/components/common/SpinnerView.vue';
import { License } from '@/models/License';
import { LicenseApplicationForm } from '@/models/LicenseApplicationForm';
import SurveyModel from '@/models/SurveyModel';

function cloneValue(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function valuesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

export default {
  components: { ModalView, SpinnerView, SurveyComponent },
  emits: ['update:modelValue', 'saved'],
  props: {
    modelValue: Boolean,
    license: {
      type: License,
      required: true
    }
  },
  data() {
    return {
      survey: null,
      originalAnswers: {},
      answerChanges: {},
      note: '',
      loadingForm: false,
      saving: false,
      error: null,
      initializationVersion: 0
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(show) {
        if (show) {
          this.initialize();
        } else {
          this.resetInitialization();
        }
      }
    },
    licenseKey() {
      if (this.modelValue) this.initialize();
    }
  },
  computed: {
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    licenseKey() {
      return `${this.license?.city?.id || ''}:${this.license?.id || ''}`;
    },
    licenseSubtitle() {
      return this.license?.number ? `License #${this.license.number}` : null;
    },
    changes() {
      return Object.entries(this.answerChanges).map(([id, value]) => ({ id, value }));
    },
    canSave() {
      return !this.loadingForm && !this.saving && this.changes.length > 0;
    }
  },
  methods: {
    resetInitialization() {
      this.initializationVersion += 1;
      this.loadingForm = false;
      this.survey = null;
      this.originalAnswers = {};
      this.answerChanges = {};
    },
    async initialize() {
      const version = ++this.initializationVersion;
      this.error = null;
      this.note = '';
      this.answerChanges = {};
      this.survey = null;
      this.loadingForm = true;

      // Let Bootstrap display the modal and its loading state before SurveyJS
      // builds what may be a large application form.
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 0));
      if (!this.modelValue || version !== this.initializationVersion) return;

      try {
        // Avoid deep-cloning the entire answer payload. Historical file answers
        // can contain large inline data URLs and are read-only in this editor.
        const originalAnswers = this.license?.customFieldData || {};
        const form = new LicenseApplicationForm(this.license.city);
        const survey = SurveyModel.createFromApplicationForm(form, {
          editable: true,
          disableFileUploads: true,
          license: this.license,
          onValueChanged: (name, value) => this.trackAnswerChange(name, value)
        });
        survey.data = originalAnswers;

        if (!this.modelValue || version !== this.initializationVersion) return;
        this.originalAnswers = originalAnswers;
        this.survey = survey;
      } catch (error) {
        if (version === this.initializationVersion) {
          this.error = error;
        }
      } finally {
        if (version === this.initializationVersion) {
          this.loadingForm = false;
        }
      }
    },
    trackAnswerChange(name, value) {
      const normalizedValue = value === undefined ? null : cloneValue(value);
      const hasOriginalValue = Object.prototype.hasOwnProperty.call(this.originalAnswers, name);
      const matchesOriginal = hasOriginalValue
        ? valuesEqual(this.originalAnswers[name], normalizedValue)
        : normalizedValue === null;
      const nextChanges = { ...this.answerChanges };
      if (matchesOriginal) {
        delete nextChanges[name];
      } else {
        nextChanges[name] = normalizedValue;
      }
      this.answerChanges = nextChanges;
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      this.error = null;
      try {
        const license = await this.license.updateApplicationAnswers(this.changes, this.note);
        this.$emit('saved', license);
        this.show = false;
      } catch (error) {
        this.error = error;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
